import { Trans, useTranslation } from "react-i18next";
import { quotationsSelector } from "../../features/quotationsSlice";
import { useAppSelector } from "../../redux/hooks";
import { OrderSummaryLoading } from "./OrderSummaryLoading";
import { Card } from "primereact/card";
import { CardHeader } from "./CardHeader";
import "./OrderSummary.css";
import { Panel } from "primereact/panel";
import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Message } from 'primereact/message';

const LEAD_STATUS = 6;

export const OrderSummary = () => {
    const {SelectedQuotationStatus, SelectedQuotation} = useAppSelector(quotationsSelector);
    const { t } = useTranslation("locale");
    const inspectionTypeDescriptionRef = useRef<Panel>(null);
    const [toggleState, setToggleState] = useState<boolean>(false);

    const onToggleDescription = (e: any) => {
        inspectionTypeDescriptionRef.current?.toggle(e);
        setToggleState(!toggleState);
    }

    const getToggleText = () => {
        return toggleState ? t("quotations.orderSummary.showLess") : t("quotations.orderSummary.showMore");
    }

    if (SelectedQuotationStatus !== "succeeded" || !SelectedQuotation) {
        return <OrderSummaryLoading />;
    }

    const splitNewLines = (text: string) => {
        return text.split("\r\n").filter(Boolean);
    }

    const getQuotationTerms = (text: string) => {
        const splitTerms = text.split("\r\n").filter(Boolean);
        return splitTerms.map((line, index) => {
            // If line contains {{idwalTermsAndConditions}}, replace text with link
            const lineHasTermsLink = line.includes("{{idwalTermsAndConditions}}");
            line = lineHasTermsLink ? line.replace("{{idwalTermsAndConditions}}", "") : line;
            return (
                <div key={`terms-${index}`}>{line} {lineHasTermsLink ? <a content="Link to Idwal Terms and Conditions" className="summary-link" href='https://www.idwalmarine.com/terms-and-conditions/' target='_blank'>Idwal Terms and Conditions</a> : <></>}</div>
            );
        });
    }

    const getInspectionPrice = () => {
        if (!SelectedQuotation.pricingDetails) {
            return 0;
        }

        return SelectedQuotation.pricingDetails.priceTotal + SelectedQuotation.pricingDetails.priceDiscountAmount;
    }

    const typeDetailsTemplate = () => {
        let headerText = "";
        if (SelectedQuotation.inspectionTypeShortName) {
            headerText = `${SelectedQuotation.inspectionType} (${SelectedQuotation.inspectionTypeShortName})`;
        } else {
            headerText = SelectedQuotation.inspectionType;
        }
        return (
            <div id="inspection-type-details" data-cy="inspection-type-details" className="grid">
                <div className="col-9">
                    <div className="message-header" id="inspection-type-details-header" data-cy="inspection-type-details-header">
                        {headerText}
                    </div>
                    <div id="inspection-type-desc" data-cy="inspection-type-desc" className={`mt-2 light-text ${!toggleState ? "truncate-text" : ""}`}>
                        {splitNewLines(SelectedQuotation.inspectionTypeDesc ?? "").map((line, index) => (
                            <div key={`type-desc-${index}`}>{line}</div>
                        ))}
                    </div>
                    <Panel collapsed={true} unstyled toggleable headerTemplate={<></>} ref={inspectionTypeDescriptionRef}>
                        <div id="quotation-terms" data-cy="quotation-terms" className="mt-3 light-text">
                            {getQuotationTerms(SelectedQuotation.quotationTerms ?? "")}
                        </div>
                    </Panel>
                    <div className="mt-2">
                        <Button className="toggle-desc-terms p-button-text" onClick={onToggleDescription}>{getToggleText()}</Button>
                    </div>
                </div>
                <div className="col-3">
                    <div className="flex justify-content-end">
                        <div className="flex flex-column">

                                <span
                                    className="text-right small-text">{t("quotations.orderSummary.inspectionPrice")}</span>
                            {
                                SelectedQuotation.status !== LEAD_STATUS ?
                                    <span  className="text-right price-text">${getInspectionPrice().toLocaleString()}</span>

                                :
                                    <span className="text-right price-text">{t("quotations.orderSummary.pending")}</span>
                            }
                    </div>
                </div>
            </div>
    </div>
    )
    }

    const generateDiscountSentence = () => {
        const discount = SelectedQuotation.pricingDetails.priceDiscountPercentage;
        return `${discount}% ${t("quotations.orderSummary.discountOff")}: $${getInspectionPrice().toLocaleString()} (USD)`;
    }

    const discountTemplate = () => {
        return (
            <div id="discount-details" data-cy="discount-details" className="grid justify-content-between">
                <div className="col-9">
                    <div className="message-header" id="discount-details-header" data-cy="discount-details-header">
                        {t("quotations.orderSummary.discountHeader")}
                    </div>
                    <div className="discount-amount mt-2" id="discount-amount" data-cy="discount-amount">
                        <div className="">{generateDiscountSentence()}</div>
                    </div>
                </div>
                <div className="col-3">
                    <div className="flex justify-content-end">
                        <div className="flex flex-column">
                            <span className="text-right small-text">{t("quotations.orderSummary.yourDiscount")}</span>
                            <span className="text-right price-text">- ${SelectedQuotation?.pricingDetails?.priceDiscountAmount?.toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Card header={CardHeader(t("quotations.orderSummary.title"))}>
            <div id="quotation-order-summary" data-cy="quotation-order-summary">
                <div id="quotation-order-summary-title" data-cy="quotation-order-summary-title">
                    {
                        SelectedQuotation.status !== LEAD_STATUS ?
                            <div
                                id="quotation-order-summary-preamble"
                                data-cy="quotation-order-summary-preamble"
                                className="light-text">
                                <Trans
                                    i18nKey={"quotations.orderSummary.preamble"}
                                    components={[
                                        <a content="Mail link for Idwal Enquiries" className="summary-link"
                                           key="contact-us" href="mailto:enquiries@idwalmarine.com" />]
                                    }
                                />
                            </div>
                            :
                            <>
                                <Message severity="warn" text={t("quotations.orderSummary.leadPreamble")} />

                                <div
                                    id="quotation-order-summary-preamble-lead"
                                    data-cy="quotation-order-summary-preamble-lead"
                                    className="pt-3 light-text">
                                    <Trans
                                        i18nKey={"quotations.orderSummary.leadPreambleTwo"}
                                        components={[
                                            <a content="Mail link for Idwal Enquiries" className="summary-link"
                                               key="contact-us" href="mailto:enquiries@idwalmarine.com" />]
                                        }
                                    />
                                </div>
                            </>
                    }

                </div>
                <div className="summary-card card-info">{typeDetailsTemplate()}</div>
                {
                    SelectedQuotation?.pricingDetails?.priceDiscountAmount > 0 ?
                        <div className="summary-card card-success">{discountTemplate()}</div>
                        :
                        <></>
                }
            </div>
        </Card>
    )
}
